import {
  SkeletonPage,
  SkeletonTabs,
  Layout,
  Card,
  BlockStack,
  SkeletonDisplayText,
  SkeletonBodyText,
} from "@shopify/polaris";
import DataTableSkeleton from "components/datatable/DataTableSkeleton";
import { ORDER_TABS, OrderTab } from "./order-tabs";

const SidebarCard = ({ lines }: { lines: number }) => (
  <Card roundedAbove="sm">
    <BlockStack gap="300">
      <SkeletonDisplayText size="small" />
      <SkeletonBodyText lines={lines} />
    </BlockStack>
  </Card>
);

const TableCard = ({ rows, columns }: { rows: number; columns: number }) => (
  <Card roundedAbove="sm">
    <BlockStack gap="300">
      <SkeletonDisplayText size="small" />
      <DataTableSkeleton rows={rows} columns={columns} />
    </BlockStack>
  </Card>
);

/**
 * Loading placeholder for the order detail page. Mirrors the real two-column
 * layout — the tab bar + the active tab's content on the left, the
 * document/signing flow cards on the right — so the page doesn't jump once the
 * order loads. The main area is tab-aware (the order tabs are URL-driven, and
 * the default is Fulfillment), so the skeleton matches whichever tab you land on.
 * A recurring order opens on Invoices instead, but only once it has loaded.
 */
export default function OrderViewSkeleton({
  selected = "fulfillment",
}: {
  selected?: OrderTab;
}) {
  return (
    <SkeletonPage fullWidth backAction>
      <Layout>
        <Layout.Section>
          <SkeletonTabs count={ORDER_TABS.length} />
          <div style={{ marginTop: "16px" }}>
            <BlockStack gap="500">
              {selected === "fulfillment" ? (
                // fulfillment: header, summary table, reservations + deliveries
                <>
                  <SidebarCard lines={1} />
                  <TableCard rows={5} columns={7} />
                  <SidebarCard lines={2} />
                  <SidebarCard lines={2} />
                </>
              ) : selected === "amendments" ? (
                // amendments: file list
                <SidebarCard lines={4} />
              ) : selected === "emails" ? (
                // emails: table
                <TableCard rows={5} columns={4} />
              ) : selected === "invoices" ? (
                // invoices: table
                <TableCard rows={4} columns={8} />
              ) : (
                // products (and product groups): tables
                <TableCard rows={6} columns={7} />
              )}
            </BlockStack>
          </div>
        </Layout.Section>
        <Layout.Section variant="oneThird">
          <BlockStack gap="500">
            {/* order document / signing flow cards */}
            <SidebarCard lines={4} />
            <SidebarCard lines={3} />
            <SidebarCard lines={3} />
          </BlockStack>
        </Layout.Section>
      </Layout>
    </SkeletonPage>
  );
}
